import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

interface PrivacyPolicyProps {
  open: boolean;
  onClose: () => void;
  onScrollToEnd?: () => void;
}

export function PrivacyPolicy({ open, onClose, onScrollToEnd }: PrivacyPolicyProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      if ((scrollTop + clientHeight) >= (scrollHeight - 70)) {
        if (typeof onScrollToEnd === 'function') {
          onScrollToEnd();
        }
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Data & Privacy Policy</DialogTitle>
        </DialogHeader>

        <div className="h-[60vh] overflow-y-auto pr-4" ref={contentRef} onScroll={handleScroll}>
          <div className="space-y-4 text-sm">
            <p className="text-gray-600">Effective: January 31, 2024</p>

            <p>
              We at ForGen AI, LLC ("Patent Loop", "we", "our" or "us") respect your privacy and are strongly committed to keeping secure any information we obtain from you or about you. This Privacy Policy describes our practices with respect to Personal Information we collect from or about you when you use our website, applications, and services (collectively, "Services").
            </p>

            <div>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 font-semibold text-gray-900 hover:text-gray-700"
              >
                Personal Information We Collect
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {isExpanded && (
                <div className="mt-2 space-y-2 text-gray-700">
                  <p className="font-medium">Personal information you provide:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Account Information: When you create an account with us, we will collect information associated with your account, including your name, contact information, and account credentials.</li>
                    <li>User Content: When you use our Services, we collect Personal Information that is included in the input, file uploads, or feedback that you provide to our Services.</li>
                    <li>Communication Information: If you communicate with us, we collect your name, contact information, and the contents of any messages you send.</li>
                  </ul>

                  <p className="font-medium mt-3">Personal information we receive automatically from your use of the Services:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Log Data: Information that your browser or device automatically sends when you use our Services.</li>
                    <li>Usage Data: Information about your use of the Services, such as the types of models used.</li>
                    <li>Device Information: Information about the device you use to access our Services, including device identifiers and browser type.</li>
                    <li>Cookies: We use cookies to operate and administer our Services, and improve your experience, but we never use any tracking or other third-party cookies.</li>
                  </ul>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">How We Use Personal Information</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700">
                <li>To provide, administer, maintain and/or analyze the Services.</li>
                <li>To communicate with you when requested by you, including to send you information about our Services and events.</li>
                <li>To prevent fraud, criminal activity, or misuse of our Services.</li>
                <li>To comply with legal obligations and legal processes.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Data Storage and Security</h3>
              <p className="text-gray-700">
                Your data is stored in a secure database and is used solely for providing you with services. It is not sold or provided to any other parties. Data will only be accessed manually via a personal request.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Use of GPT Models</h3>
              <p className="text-gray-700 mb-2">
                We use GPT models through OpenAI's Enterprise framework to provide advanced responses. You can review OpenAI's privacy policy <a href="https://openai.com/policies/privacy-policy/" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">here</a>.
              </p>
              <p className="text-gray-700">
                We also use vector databases through Weaviate's Enterprise framework. You can review Weaviate's U.S. enterprise privacy policy <a href="https://weaviate.io/service/weaviate-enterprise-agreement-us" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">here</a>.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Contact Information</h3>
              <p className="text-gray-700">
                ForGen AI, LLC<br />
                Birmingham, Michigan 48009<br />
                Email: support@forgen.ai
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
